/**
 * The list view: a plain table of rows, and a way to sort them.
 *
 * Card view is for browsing — artwork, one rave at a time. This is for
 * COMPARING: same columns, eye down a column rather than across six cards.
 *
 * **No header row.** With six raves and columns this legible — a logo, a date
 * range, a place, two counts and a button — the headings were a band of
 * uppercase grey saying what each column obviously held. The sort control
 * replaces the one job they still did, which is reordering.
 *
 * It is still a real `<table>`: the data is tabular, and a screen reader
 * should walk it as a grid rather than read a heap of unrelated spans. The
 * headers are there for that purpose, visually hidden.
 */
import { leaveTo } from '@/app/AuthProvider';
import { useEffect, useRef, useState } from 'react';
import { Button, Row, SrOnly, Txt, cx } from '@/ui';
import { RaveAdminGear } from '@/app/RaveAdminGear';
import { useLocale } from '@/app/LocaleProvider';
import { festivalUrl } from '@/lib/tenant';
import { useT, type Vars } from '@/i18n';
import { intlLocale } from '@/app/rich';
import type { Rave } from './RaveCard';
import { lineupLine, timingLabel, timingOf } from './RaveCard';
import './RaveTable.css';

export type RaveSort = 'date' | 'name' | 'saved' | 'clashes';
export type SortDir = 'asc' | 'desc';

type Sort = { id: RaveSort; label: string; natural: SortDir; asc: string; desc: string };

/**
 * Each field has the direction somebody means by it.
 *
 * "Sort by date" means soonest first; "sort by saved" means most first. A
 * single global default would be right for one of those and backwards for the
 * other, so the direction comes with the field and is then flippable.
 */
const sorts = (tr: (english: string, vars?: Vars) => string): Sort[] => [
  { id: 'date', label: tr('Date'), natural: 'asc', asc: tr('Soonest first'), desc: tr('Latest first') },
  { id: 'name', label: tr('Name'), natural: 'asc', asc: tr('A to Z'), desc: tr('Z to A') },
  { id: 'saved', label: tr('Saved acts'), natural: 'desc', asc: tr('Fewest first'), desc: tr('Most first') },
  { id: 'clashes', label: tr('Clashes'), natural: 'desc', asc: tr('Fewest first'), desc: tr('Most first') },
];

/** Sorted copy. Never mutates — the caller's array is the filter's output. */
export function sortRaves(raves: Rave[], by: RaveSort, dir: SortDir = 'asc'): Rave[] {
  /*
   * A rave with no date sorts LAST either way.
   *
   * `MAX_SAFE_INTEGER` puts it at the end ascending, and flipping the
   * comparison would drag it to the top descending — where "latest first"
   * would be headed by a rave with no date at all. Undated rows are held out
   * of the flip and appended.
   */
  const at = (r: Rave) => (r.gatesOpenAt ? new Date(r.gatesOpenAt).getTime() : null);
  const sign = dir === 'asc' ? 1 : -1;

  const dated = by === 'date' ? raves.filter((r) => at(r) !== null) : [...raves];
  const undated = by === 'date' ? raves.filter((r) => at(r) === null) : [];

  dated.sort((a, b) => {
    switch (by) {
      case 'name': return sign * a.editionName.localeCompare(b.editionName);
      case 'saved': return sign * ((a.saved ?? -1) - (b.saved ?? -1));
      case 'clashes': return sign * ((a.clashes ?? -1) - (b.clashes ?? -1));
      default: return sign * ((at(a) ?? 0) - (at(b) ?? 0));
    }
  });
  return [...dated, ...undated];
}

function Name({ r }: { r: Rave }) {
  // `mask-position` has no logical keyword: the logo sits at the start edge.
  const { direction } = useLocale();
  return (
    <a href={festivalUrl(r.subdomain, r.editionSlug)} className="rave-table-link">
      {r.logoUrl ? (
        <>
          <span
            aria-hidden="true"
            className={cx('rave-table-logo', direction === 'rtl' && 'is-rtl')}
            style={{ maskImage: `url(${r.logoUrl})`, WebkitMaskImage: `url(${r.logoUrl})` }}
          />
          {/* The name still reaches a screen reader, and a search on the page. */}
          <SrOnly>{r.editionName}</SrOnly>
        </>
      ) : (
        <Txt size={14} weight={700}>{r.editionName}</Txt>
      )}
    </a>
  );
}

/**
 * A count, or an em dash where the question does not apply.
 *
 * A rave somebody is not attending has no saved acts — different from having
 * none, which `0` would claim.
 */
function Count({ value, warn }: { value?: number; warn?: boolean }) {
  if (value === undefined) return <Txt tone="faint">—</Txt>;
  return <Txt weight={700} tabular tone={warn && value > 0 ? 'bad' : 'txt'}>{value}</Txt>;
}

function dates(r: Rave, locale: string): string {
  const tz = r.timezone ?? 'UTC';
  const fmt = (iso: string, o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(intlLocale(locale), { ...o, timeZone: tz }).format(new Date(iso));
  if (r.gatesOpenAt && r.endsAt) {
    return `${fmt(r.gatesOpenAt, { day: 'numeric', month: 'short' })} – ${fmt(r.endsAt, { day: 'numeric', month: 'short', year: '2-digit' })}`;
  }
  if (r.gatesOpenAt) return fmt(r.gatesOpenAt, { day: 'numeric', month: 'short', year: '2-digit' });
  return '—';
}

/** The sort control: an icon that opens the list of orders. */
function SortBy({ value, dir, onChange }: {
  value: RaveSort; dir: SortDir; onChange: (v: RaveSort, d: SortDir) => void;
}) {
  const tr = useT();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const SORTS = sorts(tr);
  const current = SORTS.find((s) => s.id === value) ?? SORTS[0]!;

  /*
   * A different field takes ITS natural direction; the field already chosen
   * flips. That is what every table people have used does, and it means the
   * common case — pick a field, get the obvious order — needs one tap.
   */
  const choose = (s: Sort) => {
    onChange(s.id, s.id === value ? (dir === 'asc' ? 'desc' : 'asc') : s.natural);
    // Closes on choosing, like any menu. It did not, because the close moved
    // out of the click handler when this grew a direction — so the list
    // reordered underneath a menu still sitting over it.
    setOpen(false);
  };

  const Arrow = ({ up }: { up: boolean }) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {up ? <path d="M12 19V5M6 11l6-6 6 6" /> : <path d="M12 5v14M6 13l6 6 6-6" />}
    </svg>
  );

  return (
    <div ref={wrap} className="rave-table-sort-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tr('Sort by — currently {field}, {order}', {
          field: current.label, order: dir === 'asc' ? current.asc : current.desc,
        })}
        className="rave-table-sort"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 7h12M4 12h8M4 17h4M17 11v9M17 20l-3-3M17 20l3-3" />
        </svg>
        {/* The current order AND its direction. A bare icon makes somebody
            open the menu just to find out what the list is already sorted by,
            and the field alone leaves half the answer out. */}
        {current.label}
        <Arrow up={dir === 'asc'} />
      </button>

      {open && (
        <div role="menu" className="rave-table-menu">
          {SORTS.map((s) => {
            const active = s.id === value;
            return (
              <button
                key={s.id}
                role="menuitemradio"
                aria-checked={active}
                onClick={() => choose(s)}
                className="rave-table-item"
              >
                <span className="rave-table-grow">{s.label}</span>
                {/* The active row says which way, and that tapping it again
                    turns the list around. */}
                {active && (
                  <Txt size={10.5} weight={700} className="rave-table-item-dir">
                    {dir === 'asc' ? s.asc : s.desc}
                    <Arrow up={dir === 'asc'} />
                  </Txt>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function RaveTable({
  raves, now, onAttend, busyId,
}: {
  raves: Rave[];
  now: number;
  onAttend?: (editionId: string, subdomain: string, editionSlug?: string | null) => void;
  busyId?: string | null;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const [sort, setSort] = useState<RaveSort>('date');
  const [dir, setDir] = useState<SortDir>('asc');
  const rows = sortRaves(raves, sort, dir);
  const withCounts = rows.some((r) => r.saved !== undefined || r.clashes !== undefined);

  return (
    <div className="rave-table-frame">
      <Row gap={8} className="rave-table-toolbar">
        <SortBy value={sort} dir={dir} onChange={(v, d) => { setSort(v); setDir(d); }} />
      </Row>

      {/*
        * No `min-width` on the table.
        *
        * It used to be 620px inside an `overflow-x: auto` wrapper, and the
        * page scrolled sideways by 165px at 390 anyway — a horizontal
        * scrollbar inside a page that also scrolls horizontally is two
        * problems, not one. The columns shrink instead, and the two that stop
        * being readable first drop out at their own widths.
        */}
      <table className="rave-table-table">
        <thead className="rave-table-hidden">
          <tr>
            <th scope="col">{tr('Rave')}</th>
            <th scope="col">{tr('Dates')}</th>
            <th scope="col">{tr('Location')}</th>
            <th scope="col">{tr('Saved acts')}</th>
            <th scope="col">{tr('Clashes')}</th>
            <th scope="col">{tr('Attend or open')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const t = timingOf(r, now);
            const lineup = lineupLine(r, tr);
            return (
              <tr key={r.editionId}>
                <td className="rave-table-cell rave-table-name-cell">
                  <Name r={r} />
                  {/* Under the name rather than a column of its own: it stays
                      on a phone, where the place and dates columns drop out. */}
                  {lineup && <Txt as="div" size={11} tone="muted" tabular className="rave-table-lineup">{lineup}</Txt>}
                  {(r.attending || t.tone !== 'soon') && (
                    <Txt size={9.5} weight={800} track={0.06} tone={r.attending ? 'good' : 'faint'} className="rave-table-state">
                      {r.attending ? tr('ATTENDING') : timingLabel(t, tr).toUpperCase()}
                    </Txt>
                  )}
                </td>
                <td className="rt-dates rave-table-cell rave-table-dates-cell">
                  {dates(r, locale)}
                </td>
                <td className="rt-place rave-table-cell rave-table-place-cell">
                  {[r.venue, r.country].filter(Boolean).join(' · ') || '—'}
                </td>
                {/* A column of dashes says nothing: the counts are drawn only when
                    some rave here has them — never in an admin's list (2026-10-11). */}
                {withCounts && <td className="rave-table-cell rave-table-num-cell"><Count value={r.saved} /></td>}
                {withCounts && <td className="rave-table-cell rave-table-num-cell"><Count value={r.clashes} warn /></td>}
                <td className="rave-table-cell rave-table-action-cell">
                  <Row gap={6} className="rave-table-actions">
                    {r.attending ? (
                      <Button variant="go" onClick={() => void leaveTo(festivalUrl(r.subdomain, r.editionSlug))}>
                        {tr('Open')}
                      </Button>
                    ) : onAttend ? (
                      <Button
                        variant="primary"
                        loading={busyId === r.editionId}
                        loadingLabel="…"
                        onClick={() => onAttend(r.editionId, r.subdomain, r.editionSlug)}
                      >
                        {tr('Attend')}
                      </Button>
                    ) : null}
                    {/* Same rule as the card: a rave you administer carries a
                        gear wherever it is drawn. Nothing for anybody else. */}
                    <RaveAdminGear editionId={r.editionId} size={30} />
                  </Row>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
